// Copyright © 2019-2026 Focal Systems. All rights reserved.

/** `day_of_week` is `*`, a single abbreviation (`Sun`), or a comma list (`Mon,Tue,Wed`). */
export function parseDays(dayOfWeek: string | undefined): string[] {
  if (!dayOfWeek || dayOfWeek === "*") return [];
  return dayOfWeek
    .split(",")
    .map((d) => d.trim())
    .filter(Boolean);
}

/** Stored as a `HH:MM:SS` time-of-day, not a timestamp — sliced rather than date-formatted. */
export function formatCutOff(value: string) {
  return value.slice(0, 5);
}

/**
 * Aggregation buckets on a 15-minute grid (`INTERVAL_IN_MINUTES` in the backend), so a cut-off off
 * that grid has no bucket to line up with and can never fire.
 */
export const STEP_MINUTES = 15;

/** Whether a `HH:MM` value sits on the 15-minute grid. */
export function isOnStep(value: string): boolean {
  return Number(value.slice(3, 5)) % STEP_MINUTES === 0;
}

/** Snap a `HH:MM` value to the nearest 15-minute mark. Anything unparseable is returned untouched. */
export function roundToStep(value: string): string {
  const parts = value.split(":");
  const hours = Number(parts[0]);
  const minutes = Number(parts[1]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return value;
  // Wrapping keeps the result a real time of day: 23:53 lands on 00:00 rather than an invalid 24:00.
  const snapped = (Math.round((hours * 60 + minutes) / STEP_MINUTES) * STEP_MINUTES) % (24 * 60);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(Math.floor(snapped / 60))}:${pad(snapped % 60)}`;
}
